import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { api } from "../../lib/api";
import { money, num } from "../../lib/format";
import type { PartnerDetail, PartnerPayout, ReferralDTO } from "../../../shared/types";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { PnRow } from "../../../shared/phone/Row";
import { Icon } from "../../components/ui/Icon";
import { MarkPaidSheet, type PaidRecord } from "./MarkPaidSheet";
import { CopyRow, dayLabel, Figures, linkParts, Monogram, plural, SayToast, STAGE_LABEL } from "./parts";
import { methodSummary } from "./payout";
import { usePartnerDetail } from "./usePartnerDetail";
import { useSay } from "./useSay";

/** Referrals shown before "See all". */
const FIRST_REFERRALS = 5;

/** Where the owed money stands, as the pill beside "Owed now". */
function OwedPill({ payout }: { payout: PartnerPayout }) {
  if (payout.state === "ready") return <span className="pn-pill acc"><i />Ready to pay</span>;
  if (payout.state === "blocked") return <span className="pn-pill warn"><i />On hold</span>;
  if (payout.state === "below_min") return <span className="pn-pill"><i />Below the minimum</span>;
  if (payout.lastPaidAt) return <span className="pn-pill good"><i />Paid {dayLabel(payout.lastPaidAt)}</span>;
  return null;
}

const STATUS: Record<string, [string, string]> = {
  active: ["good", "Active"],
  pending: ["", "Pending"],
  paused: ["warn", "Paused"],
  rejected: ["danger", "Rejected"],
};

/** "the tax form and a payout method": what the partner still owes the program. */
function missingDocs(d: PartnerDetail): string {
  const missing: string[] = [];
  if (!d.docStatus.agreement) missing.push("the signed agreement");
  if (!d.docStatus.tax) missing.push("the tax form");
  if (!d.docStatus.payoutMethod) missing.push("a payout method");
  if (missing.length < 2) return missing.join("");
  return `${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}`;
}

function ReferralRow({ r }: { r: ReferralDTO }) {
  const label = r.personLabel ?? "Someone";
  const when = dayLabel(r.joinedAt);
  const value = r.reversed ? "Reversed" : r.earnedCents > 0 ? money(r.earnedCents) : "Pending";
  return (
    <div className="pn-row static pa-ref">
      <Monogram name={label} small />
      <span className="m">
        <span className="t">{label}</span>
        <span className="s">
          {when ? `Joined ${when}, ` : ""}
          {STAGE_LABEL[r.stage]}
        </span>
      </span>
      <span className={r.cleared && !r.reversed && r.earnedCents > 0 ? "pa-rv good" : "pa-rv"}>{value}</span>
    </div>
  );
}

/**
 * One partner on a phone or an iPad: the name as the large title, the link that copies, what is owed with
 * the four numbers, the documents and the referrals, and Mark paid pinned at the bottom. In the iPad's
 * Partners split it is the right pane (`pane`), with no back.
 */
export function PartnerScreen({ id, pane = false, onChanged }: { id: string; pane?: boolean; onChanged?: () => void }) {
  const { detail, loading, notFound, err, refetch } = usePartnerDetail(id);
  const [markOpen, setMarkOpen] = useState(false);
  const [all, setAll] = useState(false);
  const { toast, say, copied } = useSay();
  const from = (useLocation().state as { from?: string } | null)?.from;
  const top = useRef<HTMLDivElement>(null);

  // A partner picked in the iPad's split opens at its top, with its referrals folded.
  useEffect(() => {
    setAll(false);
    setMarkOpen(false);
    if (pane) top.current?.closest(".pn-pane")?.scrollTo({ top: 0 });
  }, [id, pane]);

  const record = useCallback(
    async (r: PaidRecord) => {
      if (!detail) return;
      const name = `${detail.partner.firstName} ${detail.partner.lastName}`.trim();
      await api.partners.markPaid(detail.partner.id, { ...r, methodSnapshot: detail.payoutMethod });
      setMarkOpen(false);
      say(`Recorded ${money(r.amountCents)} to ${name}`);
      await refetch().catch(() => {});
      onChanged?.();
    },
    [detail, refetch, say, onChanged],
  );

  const name = detail ? `${detail.partner.firstName} ${detail.partner.lastName}`.trim() : "";
  const productId = detail?.program.productId;
  const partnersPath = productId ? `/products/${encodeURIComponent(productId)}/partners` : "/products";
  const back = pane ? undefined : from ? { to: from, label: "Pay partners" } : { to: partnersPath, label: "Partners" };
  const status = detail ? (STATUS[detail.partner.status] ?? STATUS.pending) : null;

  if (!detail) {
    return (
      <div className="pn-screen" ref={top}>
        <PhoneNav title="" back={back} />
        {notFound ? (
          <div className="pn-empty">
            <b>Partner not found</b>
            This partner does not exist, or they belong to another account.
          </div>
        ) : err ? (
          <div className="pn-empty">
            <b>Couldn't load the partner</b>
            {err}
          </div>
        ) : loading ? (
          <div className="pn-empty">Loading the partner…</div>
        ) : null}
      </div>
    );
  }

  const { partner, program, kpis, payout, docStatus, documents, payoutMethod, referrals } = detail;
  const link = linkParts(program.linkBase, partner.slug);
  const agreement = documents.find((d) => d.kind === "agreement" && d.signedAt) ?? null;
  const tax = documents.find((d) => d.kind === "tax") ?? null;
  const allOnFile = docStatus.agreement && docStatus.tax && docStatus.payoutMethod;
  const canPay = payout.state === "ready" && payout.readyCents > 0;
  const shown = all ? referrals : referrals.slice(0, FIRST_REFERRALS);

  return (
    <div className="pn-screen" ref={top}>
      <PhoneNav
        title={name}
        large
        back={back}
        sub={
          <span className="pa-psub">
            {status ? (
              <span className={`pn-pill ${status[0]}`}>
                <i />
                {status[1]}
              </span>
            ) : null}
            <span>{partner.email}</span>
          </span>
        }
      />
      <div className="pn-stack">
        <div className="pn-grp">
          <CopyRow big icon="link" label="Referral link" value={link.full} shown={link.shown} onCopied={copied} />
        </div>

        <div className="pn-grp">
          <div className="pn-row static pa-owe">
            <span className="m">
              <span className="s">Owed now</span>
              <span className="pa-big">{money(payout.owedCents)}</span>
            </span>
            <OwedPill payout={payout} />
          </div>
          <Figures
            items={[
              [num(kpis.clicks), "Clicks"],
              [num(kpis.regs), "Signups"],
              [num(kpis.sales), "Customers"],
              [money(kpis.earnedCents), "Earned"],
            ]}
          />
        </div>

        <section>
          <div className="pn-sh">
            <h2>Documents</h2>
          </div>
          <div className="pn-grp">
            <PnRow
              icon={<Icon name="doc" />}
              title="Partner agreement"
              sub={agreement ? `Signed ${dayLabel(agreement.signedAt)}${agreement.docVersion ? ` · version ${agreement.docVersion}` : ""}` : "Not signed yet"}
              onClick={agreement ? () => window.open(api.agreements.partnerDocumentPdfUrl(partner.id, agreement.id), "_blank", "noopener") : undefined}
              chevron={!!agreement}
            />
            <PnRow
              icon={<Icon name="doc" />}
              iconTone="plain"
              title="Tax form"
              sub={docStatus.tax ? (tax?.signedAt ? `Signed ${dayLabel(tax.signedAt)}` : "On file") : "Not provided yet"}
              value={docStatus.tax ? "On file" : "Missing"}
            />
            <PnRow
              icon={<Icon name="wallet" />}
              iconTone="plain"
              title="Payout method"
              sub={docStatus.payoutMethod ? methodSummary(payoutMethod) : "Not added yet"}
              value={docStatus.payoutMethod ? "Saved" : "Missing"}
            />
            <PnRow
              to={`${partnersPath}?program=documents`}
              icon={<Icon name="gear" />}
              iconTone="plain"
              title="Manage required documents"
              className="accent"
            />
          </div>
          <p className="pn-note">
            {allOnFile
              ? canPay
                ? "Everything required is on file, so payouts are unlocked."
                : "Everything required is on file."
              : `Waiting on ${partner.firstName} for ${missingDocs(detail)}. Payouts unlock once all three are on file.`}
          </p>
        </section>

        <section>
          <div className="pn-sh">
            <h2>Referrals</h2>
            <span className="pa-shn">Last click</span>
          </div>
          <div className="pn-grp">
            {shown.length ? (
              shown.map((r) => <ReferralRow key={r.id} r={r} />)
            ) : (
              <div className="pn-row static">
                <span className="m">
                  <span className="s">No referrals yet. They show up as people sign up through {partner.firstName}'s link.</span>
                </span>
              </div>
            )}
            {referrals.length > FIRST_REFERRALS ? (
              <button type="button" className="pn-row accent pa-all" onClick={() => setAll((v) => !v)}>
                {all ? `Show the latest ${FIRST_REFERRALS}` : `See all ${plural(referrals.length, "referral")}`}
              </button>
            ) : null}
          </div>
        </section>
      </div>

      {canPay ? (
        <div className="pn-foot">
          <button type="button" className="pn-btn pri wide" onClick={() => setMarkOpen(true)}>
            Mark {money(payout.readyCents)} paid
          </button>
        </div>
      ) : payout.owedCents > 0 ? (
        <div className="pn-foot">
          <p className="pa-block">
            <Icon name="lock" />
            <span>{payout.reason ?? "This money cannot be sent yet."}</span>
          </p>
        </div>
      ) : null}

      {markOpen ? (
        <MarkPaidSheet
          name={name}
          owedCents={payout.readyCents}
          method={payoutMethod}
          onClose={() => setMarkOpen(false)}
          onRecord={record}
          onCopied={copied}
        />
      ) : null}
      <SayToast toast={toast} />
    </div>
  );
}
